import dayjs from 'dayjs'
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'

import { landAxios } from '@Root/utils/landAxios'
import MyHouseInsuranceParam from '@Types/entities/MyHouseInsuranceParam'
import InsuranceHouseResponse from '@Types/entities/InsuranceHouseResponse'
import ApiResponse from '@Types/inner/ApiResponse'

export interface InsuranceParam {
  houseNo: number
  insuranceSequence: number
}

interface SubscriptionInsurance {
  houseLabel: string
  houseNo: number
  productSequence: number
  companyCode: string
  startYearMonth: string
  endYearMonthText: string
  subscriptionPeriod: number
  deleteConfirmFlag: boolean
}

export type InsuranceSubscribedState = ApiResponse<SubscriptionInsurance>

const initialSubscribedState: InsuranceSubscribedState = {
  houseLabel: '',
  houseNo: 0,
  productSequence: 0,
  companyCode: '',
  startYearMonth: '',
  endYearMonthText: '',
  subscriptionPeriod: 12,
  deleteConfirmFlag: false,
  isSuccess: false,
  isPending: false,
}

export const fetchInsuranceRegister = createAsyncThunk('myhouse/insurance/register', async (params: MyHouseInsuranceParam) => {
  return await (await landAxios.post(`myhouse/${params.houseNo}/insurance`, params)).data
})

export const fetchInsuranceModify = createAsyncThunk(
  'myhouse/insurance/modify',
  async ({ param, insuranceSequence }: { param: MyHouseInsuranceParam; insuranceSequence: number }) => {
    return await (await landAxios.patch(`myhouse/${param.houseNo}/insurance/${insuranceSequence}`, param)).data
  },
)

export const fetchInsuranceDelete = createAsyncThunk(
  'myhouse/fetchInsuranceDelete',
  async (param: InsuranceParam) => await (await landAxios.delete(`myhouse/${param.houseNo}/insurance/${param.insuranceSequence}`)).data,
)

const setExpiredYearMonthText = (state: InsuranceSubscribedState): void => {
  if (!state.startYearMonth) {
    state.endYearMonthText = ''
  } else {
    state.endYearMonthText = `${dayjs(state.startYearMonth + '01')
      .add(state.subscriptionPeriod, 'month')
      .format('YYYY년 MM월')}`
  }
}

const myhouseInsuranceRegisterSlice = createSlice({
  name: 'myhouseSubscribedInsurance',
  initialState: initialSubscribedState,
  reducers: {
    initializeState: (state: InsuranceSubscribedState) => {
      Object.assign(state, initialSubscribedState)
    },
    updateHouseLabel: (state: InsuranceSubscribedState, action: PayloadAction<InsuranceHouseResponse>) => {
      const insuranceHouse = action.payload
      const expsAddress = !!insuranceHouse.expsArea ? insuranceHouse.expsArea : ''
      const abbrAddress = !!insuranceHouse.abbrAddress ? insuranceHouse.abbrAddress : ''
      const expsAbbrAddress = (!!expsAddress ? expsAddress + ' ' : '') + abbrAddress

      const buildNameWrap = !!insuranceHouse.buildName ? insuranceHouse.buildName + '동 ' : ''
      const hoNameWrap = !!insuranceHouse.hoName ? insuranceHouse.hoName + '호' : ''
      const addrWrap = !!abbrAddress ? abbrAddress : ''
      const addrWrapConcat = !buildNameWrap && !hoNameWrap ? addrWrap : ''

      if (!!insuranceHouse.complexNo && !!insuranceHouse.complexName) {
        state.houseLabel = insuranceHouse.complexName + ' ' + buildNameWrap + hoNameWrap + addrWrapConcat
      }
      if (!!insuranceHouse.complexNo && !insuranceHouse.complexName) {
        state.houseLabel = expsAbbrAddress + ' ' + buildNameWrap + hoNameWrap + addrWrapConcat
      }
      if (!insuranceHouse.complexNo && !!insuranceHouse.complexName) {
        state.houseLabel = insuranceHouse.complexName + ' ' + buildNameWrap + hoNameWrap + addrWrapConcat
      }
      if (!insuranceHouse.complexNo && !insuranceHouse.complexName) {
        state.houseLabel = expsAbbrAddress + ' ' + buildNameWrap + hoNameWrap
      }
    },
    updateHouseNo: (state: InsuranceSubscribedState, action: PayloadAction<number>) => {
      state.houseNo = action.payload
    },
    updateCompanyCode: (state: InsuranceSubscribedState, action: PayloadAction<string>) => {
      state.companyCode = action.payload
    },
    updateProductSequence: (state: InsuranceSubscribedState, action: PayloadAction<number>) => {
      state.productSequence = action.payload
    },
    updateStartYearMonth: (state: InsuranceSubscribedState, action: PayloadAction<string>) => {
      state.startYearMonth = action.payload
      setExpiredYearMonthText(state)
    },
    updateSubscriptionPeriod: (state: InsuranceSubscribedState, action: PayloadAction<number>) => {
      state.subscriptionPeriod = action.payload
      setExpiredYearMonthText(state)
    },
  },
  extraReducers: {
    [fetchInsuranceRegister.fulfilled.type]: (state: InsuranceSubscribedState, action: PayloadAction<number>) => {
      state.isPending = false
      state.isSuccess = true
    },
    [fetchInsuranceRegister.pending.type]: (state: InsuranceSubscribedState, action: PayloadAction<number>) => {
      state.isPending = true
      state.isSuccess = false
    },
    [fetchInsuranceModify.fulfilled.type]: (state: InsuranceSubscribedState, action: PayloadAction<number>) => {
      state.isPending = false
      state.isSuccess = true
    },
    [fetchInsuranceModify.pending.type]: (state: InsuranceSubscribedState, action: PayloadAction<number>) => {
      state.isPending = true
      state.isSuccess = false
    },
    [fetchInsuranceDelete.fulfilled.type]: (state: InsuranceSubscribedState, action: PayloadAction<number>) => {
      state.deleteConfirmFlag = true
    },
    [fetchInsuranceDelete.pending.type]: (state: InsuranceSubscribedState, action: PayloadAction<number>) => {
      state.deleteConfirmFlag = false
    },
  },
})

export const {
  initializeState,
  updateHouseLabel,
  updateHouseNo,
  updateCompanyCode,
  updateProductSequence,
  updateStartYearMonth,
  updateSubscriptionPeriod,
} = myhouseInsuranceRegisterSlice.actions
export default myhouseInsuranceRegisterSlice.reducer
